import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import LocationPickerMap from "../../PersonalizedQuotePage/LocationPikerMap/LocationPikerMap";
import { updateBookingAddress, getOneUpcomingBookingById } from "../../../utils/APIs/bookingApis";
import Loader from "../../../Template/Loader/Loader";
import "./QuoteEventAddress.css";

const QuoteEventAddress = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [addressData, setAddressData] = useState({
    location: "",
    lat: "",
    lng: "",
    streetName: "",
    city: "",
    state: "",
    postalCode: "",
    flatOrHouseNo: "",
  });

  useEffect(() => {
    if (!bookingId) {
      setInitialFetchDone(true);
      return;
    }

    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        const res = await getOneUpcomingBookingById(bookingId);
        const bookingData = res?.data?.data;
        if (bookingData && (bookingData.address || bookingData.location || bookingData.city)) {
          setAddressData((prev) => ({
            ...prev,
            location: bookingData.address || bookingData.location || prev.location,
            lat: bookingData.lat || prev.lat,
            lng: bookingData.lng || prev.lng,
            streetName: bookingData.streetName || prev.streetName,
            city: bookingData.city || prev.city,
            state: bookingData.state || prev.state,
            postalCode: bookingData.postalCode || prev.postalCode,
            flatOrHouseNo: bookingData.flatOrHouseNo || prev.flatOrHouseNo,
          }));
        }
      } catch (err) {
        console.error("Error fetching booking details:", err);
      } finally {
        setLoading(false);
        setInitialFetchDone(true);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  useEffect(() => {
    // Push a dummy state so back button triggers popstate
    window.history.pushState(null, "", window.location.href);

    const handleBackButton = () => {
      navigate("/myProfile", { state: { openTab: "upcoming" }, replace: true });
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [navigate]);

  const handleLocationSelect = (data) => {
    setAddressData((prev) => ({
      ...prev,
      location: data.location || "",
      lat: data.lat || "",
      lng: data.lng || "",
      streetName: data.streetName || "",
      city: data.city || "",
      state: data.state || "",
      postalCode: data.postalCode || "",
    }));
  };

  const clearBookingReturnUrls = () => {
    sessionStorage.removeItem("bookingDetailsReturnUrl");
    sessionStorage.removeItem("quoteReturnUrl");
  };

  const handleBack = () => {
    clearBookingReturnUrls();
    navigate("/myProfile", { state: { openTab: "upcoming" }, replace: true });
  };

  const handleSubmit = async () => {
    if (!bookingId) {
      toast.error("Booking not found. Please try again from your profile.");
      return;
    }
    if (!addressData.location || !addressData.lat || !addressData.lng) {
      toast.error("Please select your event location on the map.");
      return;
    }
    if (!addressData.flatOrHouseNo?.trim()) {
      toast.error("Please enter flat / house number.");
      return;
    }
    if (!addressData.city || !addressData.state || !addressData.postalCode) {
      toast.error("Complete address could not be detected. Pick a clearer pin on the map.");
      return;
    }
    if (!/^[0-9]{6}$/.test(String(addressData.postalCode))) {
      toast.error("Postal code must be 6 digits.");
      return;
    }

    try {
      setLoading(true);
      const res = await updateBookingAddress(bookingId, {
        flatOrHouseNo: addressData.flatOrHouseNo.trim(),
        streetName: addressData.streetName,
        city: addressData.city,
        state: addressData.state,
        postalCode: addressData.postalCode,
        lat: addressData.lat,
        lng: addressData.lng,
        address: addressData.location,
        location: addressData.location,
      });

      if (res?.data?.success) {
        toast.success("Event address saved successfully.");
        clearBookingReturnUrls();
        navigate("/myProfile", { state: { openTab: "upcoming" }, replace: true });
      } else {
        toast.error(res?.data?.message || "Could not save address.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save address.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quote-event-address-page">
      {loading && <Loader />}

      <button
        type="button"
        className="quote-event-back-btn"
        onClick={handleBack}
        aria-label="Go back"
      >
        <FaArrowLeft /> Back
      </button>

      <div className="quote-event-address-inner">
        <div className="quote-event-address-card">
        <h1>Confirm event address</h1>
        <p className="quote-event-address-sub">
          Pin your exact event location on the map
        </p>

        <div className="quote-event-address-body">
          <div className="quote-event-map-wrap">
            {initialFetchDone && (
              <LocationPickerMap 
                onSelect={handleLocationSelect} 
                isOpen={true} 
                defaultLat={addressData.lat}
                defaultLng={addressData.lng}
                disableInitialFetch={!!(addressData.lat || addressData.city || addressData.location)}
              />
            )}
          </div>

            <div className="quote-event-fields">
              <label>
                Full address
                <input type="text" value={addressData.location} readOnly placeholder="Select on map" />
              </label>

              <label>
                Flat / house no. *
                <input
                  type="text"
                  value={addressData.flatOrHouseNo}
                  onChange={(e) =>
                    setAddressData((prev) => ({ ...prev, flatOrHouseNo: e.target.value }))
                  }
                  placeholder="e.g. 12B, Green Park"
                />
              </label>

              <div className="quote-event-fields-row">
                <label>
                  Street
                  <input type="text" value={addressData.streetName} readOnly />
                </label>
                <label>
                  City
                  <input type="text" value={addressData.city} readOnly />
                </label>
              </div>

              <div className="quote-event-fields-row">
                <label>
                  State
                  <input type="text" value={addressData.state} readOnly />
                </label>
                <label>
                  Postal code
                  <input type="text" value={addressData.postalCode} readOnly />
                </label>
              </div>
            </div>
          </div>

        <button type="button" className="quote-event-submit-btn" onClick={handleSubmit}>
          Save address &amp; continue
        </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteEventAddress;
